import { useState } from 'react'

import dayjs from 'dayjs'
import { Link } from 'react-router-dom'

import { hasScopesPermission } from '@tribeplatform/gql-client'
import { Image, Member } from '@tribeplatform/gql-client/types'
import { BannerReposition } from '@tribeplatform/react-components/Picker'
import { useAuthMember, useNetwork } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Link as UILink } from '@tribeplatform/react-ui-kit/Link'

import { ProfilePicture } from '../Settings/ProfilePicture'
import { ProfileSettingsModal } from '../Settings/ProfileSettingsModal'
import { BannerPlaceholder } from './BannerPlaceholder'

const getWelcomeMessageText = () => {
  const hour = parseInt(dayjs().format('HH'), 10)
  let message
  if (hour < 12) message = 'Good morning'
  else if (hour < 18) message = 'Good afternoon'
  else message = 'Good evening'
  return message
}

export const AuthMemberCard = ({ member }: { member: Member }) => {
  const [profileModal, setProfileModal] = useState(false)
  const { data: network } = useNetwork()
  const { data: authMember, isGuest } = useAuthMember({
    fields: 'default',
    useQueryOptions: {
      enabled: !member,
    },
  })
  if (!member && !isGuest) member = authMember

  if (!member) {
    const [canJoinNetwork] = hasScopesPermission(network, ['joinNetwork'])
    return (
      <Card className="overflow-hidden block-welcome-member block-welcome-guest">
        <Card.Content>
          <div className="text-xl mb-3 text-basicSurface-900 font-medium truncate">
            {getWelcomeMessageText()} 👋
          </div>
          <div className="flex flex-col space-y-3 mb-5">
            <p>Welcome to {network?.name}</p>
            <p>
              Connect, share, and engage with community and build relationships.
            </p>
            {canJoinNetwork && (
              <p>
                Please log in if you are already a member or sign up for an
                account.
              </p>
            )}
          </div>
          <div className="flex flex-col space-y-3">
            {canJoinNetwork ? (
              <>
                <Button
                  as={Link}
                  to="/auth/signup"
                  size="lg"
                  variant="primary"
                  fullWidth
                >
                  Join the community
                </Button>
                <Button
                  as={Link}
                  to="/auth/login"
                  size="lg"
                  variant="outline"
                  fullWidth
                >
                  Already a member?
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm">This is an invitation-only community.</p>
                <Button
                  as={Link}
                  to="/auth/login"
                  size="lg"
                  variant="outline"
                  fullWidth
                >
                  Already a member?
                </Button>
              </>
            )}
          </div>
        </Card.Content>
      </Card>
    )
  }

  return (
    <>
      <Card className="overflow-hidden block-welcome-member">
        <div className="h-20 w-full object-cover lg:h-28 group relative overflow-hidden">
          {member?.banner ? (
            <BannerReposition edit={false} banner={member?.banner as Image} />
          ) : (
            <div className="h-32 w-full lg:h-48 bg-main-200">
              <BannerPlaceholder member={member} />
            </div>
          )}
        </div>
        <div className="-mt-16 flex justify-center">
          <ProfilePicture
            member={member}
            size="2xl"
            editable={!member?.profilePicture}
            className="ring-2 bg-surface-50 ring-offset-surface-50 ring-surface-50"
          />
        </div>
        <Card.Content className="overflow-hidden text-center">
          <div className="text-sm mb-5 text-basicSurface-400">
            {getWelcomeMessageText()},
          </div>
          <div className="text-xl mb-1 text-basicSurface-900 font-medium truncate">
            <Link to={`/member/${member?.id}`}>{member?.name}</Link>
          </div>
          {member?.tagline ? (
            <div className="text-base mb-5 text-basicSurface-400 line-clamp-2">
              {member?.tagline}
            </div>
          ) : (
            <div className="text-base mb-5 text-basicSurface-400 truncate">
              <UILink onClick={() => setProfileModal(true)}>Add tagline</UILink>
            </div>
          )}

          <Button
            className="mt-8"
            size="lg"
            as={Link}
            to={`/member/${member?.id}`}
            variant="outline"
            fullWidth
          >
            View profile
          </Button>
        </Card.Content>
      </Card>
      {profileModal && (
        <ProfileSettingsModal
          member={member}
          onClose={() => setProfileModal(false)}
          open={profileModal}
        />
      )}
    </>
  )
}
